//* Importing react hooks
import React, { useState, useEffect, useCallback } from "react";

//* Importing react-router hooks
import { useParams, useNavigate } from "react-router-dom";

//* Importing components from react-bootstrap
import { Card, Alert, Button, Tabs, Tab } from "react-bootstrap";

//* Importing icons from lucide-react
import { AlertCircle, RefreshCcw } from "lucide-react";

//* Importing custom hooks from useCourses
import { useCourses } from "../../../hooks/courses/useCourses";

//* Import components
import { Loading } from "../../../component/common/Loading";
import { CourseHeader } from "../view/base/CourseHeader";
import { TeamsTab } from "../../../component/base/view/TeamsTab";
import { StudentsTab } from "../../../component/base/view/StudentsTab";

//* Import types
import { type CoursesDetails } from "../../../hooks/courses/types/type";

export function CourseViewDetails(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [courseDetails, setCourseDetails] = useState<CoursesDetails | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem("token") || "";
  const { getCourseById } = useCourses(token);

  // Estados de filtro e paginação para estudantes
  const [studentFilters, setStudentFilters] = useState({
    searchTermStudents: "",
    studentFilter: "all" as "all" | "active" | "inactive",
    pageStudents: 1,
    perPageStudents: 10,
  });

  // Estados de filtro e paginação para turmas
  const [teamFilters, setTeamFilters] = useState({
    searchTermTeams: "",
    pageTeams: 1,
    perPageTeams: 10,
  });

  // Estado para controlar a aba ativa
  const [activeTab, setActiveTab] = useState("teams"); // 'teams' ou 'students'

  const fetchCourseDetails = useCallback(async () => {
    if (token && id) {
      setLoading(true);
      setError(null);
      try {
        const details = await getCourseById(id, {
          ...studentFilters,
          ...teamFilters,
        });
        setCourseDetails(details);
      } catch (err) {
        console.error("Erro ao buscar detalhes da curso:", err);
        setError(
          "Ocorreu um erro ao carregar os detalhes da curso. Tente novamente."
        );
      } finally {
        setLoading(false);
      }
    }
  }, [id, getCourseById, token, studentFilters, teamFilters]);

  useEffect(() => {
    fetchCourseDetails();
  }, [fetchCourseDetails]);

  const handleBack = () => {
    navigate("/courses");
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Card className="mb-4 shadow-sm">
      {courseDetails && (
        <>
          <CourseHeader coursesDetails={courseDetails} />
          <Card.Body>
            {error && (
              <Alert
                variant="danger"
                className="d-flex align-items-center"
                role="alert"
              >
                <AlertCircle className="me-2" />
                <span>{error}</span>
                <Button
                  variant="outline-light"
                  size="sm"
                  className="ms-auto"
                  onClick={fetchCourseDetails}
                  aria-label="Tentar novamente"
                >
                  <RefreshCcw size={16} className="me-1" />
                  Tentar Novamente
                </Button>
              </Alert>
            )}
            {!error && (
              <>
                <Tabs
                  id="course-details-tabs"
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k || "teams")}
                  className="mb-3"
                  fill
                >
                  <Tab eventKey="teams" title="Turmas" aria-label="Turmas">
                    <TeamsTab
                      teamsData={courseDetails.teams}
                      totalTeams={courseDetails.totalTeams}
                      teamFilters={teamFilters}
                      setTeamFilters={setTeamFilters}
                      reloadData={fetchCourseDetails}
                    />
                  </Tab>
                  <Tab eventKey="students" title="Alunos" aria-label="Alunos">
                    <StudentsTab
                      studentsData={courseDetails.students}
                      totalStudents={courseDetails.totalStudents}
                      totalActiveStudents={courseDetails.totalActiveStudents}
                      studentFilters={studentFilters}
                      setStudentFilters={setStudentFilters}
                      reloadData={fetchCourseDetails}
                    />
                  </Tab>
                </Tabs>

                <div className="d-flex justify-content-end mt-4">
                  <Button
                    variant="primary"
                    onClick={handleBack}
                    aria-label="Voltar para a lista de cursos"
                  >
                    Voltar
                  </Button>
                </div>
              </>
            )}
          </Card.Body>
        </>
      )}
    </Card>
  );
}
